"use client";
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { GroupedCourse, ScheduledCourse } from "@/app/types/course";
import { RutgersCourse } from "@/app/types/RutgersCourse";
import { openSectionsService } from "@/app/services/api";
import { CORE_CODES, type CoreCode } from "@/app/services/courseCatalogShared";
import { useCatalogCourses } from "@/app/services/useCatalogCourses";
import { cn, calculateSimilarity, partialMatch, fuzzyMatchScore } from "@/app/lib/utils";
import { useDebounce } from 'use-debounce';
import { checkPrerequisites } from "@/app/services/prerequisiteChecker";
import { useCourseContextMenu } from "@/app/hooks/useCourseContextMenu";
import ContextMenu from "./ContextMenu";

// Interfaces
export interface CourseSearchProps {
  onSelectCourse: (course: GroupedCourse) => void;
  selectedCourses: GroupedCourse[];
  onRemoveCourse: (courseId: string) => void;
  onAddCourse?: (course: GroupedCourse) => void;
  onCoursesChange?: (courses: GroupedCourse[]) => void;
  onAddCoreBlock: (coreCode: CoreCode) => void;
  selectedCoreBlocks: { coreCode: CoreCode; selectedCourse?: RutgersCourse }[];
  showSections?: boolean;
  completedCourses?: RutgersCourse[];
  maxResults?: number; // limit visible results; defaults to 5
}

interface CourseWithNumber extends GroupedCourse {
  classNumber?: string;
  department?: string;
  coreRequirements?: CoreCode[];
}

// Cache for course search results
const courseSearchCache = new Map<string, CourseWithNumber[]>();

// Clear cache periodically to prevent memory leaks
setInterval(() => {
  courseSearchCache.clear();
}, 5 * 60 * 1000); // Clear every 5 minutes

// Custom hook for course search logic
const useCourseSearch = (completedCourses: RutgersCourse[] = []) => {
  const allCourses = useCatalogCourses('fall-2026');
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [selectedCore, setSelectedCore] = useState<CoreCode | "">("");
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchOpenSections = async () => {
      await openSectionsService.refreshOpenSections();
      setOpenSections(openSectionsService.openSectionsCache.get('current') || new Set());
    };
    fetchOpenSections();
    const interval = setInterval(fetchOpenSections, 60000);
    return () => clearInterval(interval);
  }, []);

  const searchResults = useMemo(() => {
    const term = debouncedSearchQuery.toLowerCase().trim();
    if (!term && !selectedCore) return [];
    if (allCourses.length === 0) return [];

    // Create cache key from search parameters
    const cacheKey = `${term}|${selectedCore}|${completedCourses.length}|${allCourses.length}`;
    
    // Check cache first
    if (courseSearchCache.has(cacheKey)) {
      return courseSearchCache.get(cacheKey)!;
    }

    const all = allCourses;
    const seen = new Set<string>();
    
    // Phase 1: Direct matches
    // Phase 2: Fuzzy matches (if no direct matches)
    // Phase 3: Partial matches (if no fuzzy matches)
    const directMatches: CourseWithNumber[] = [];
    const fuzzyMatches: Array<{ course: CourseWithNumber; score: number }> = [];
    const partialMatches: CourseWithNumber[] = [];

    for (const c of all) {
      if (seen.has(c.baseId)) continue;
      
      if (!term) {
        // If no search term, skip (or handle core requirement filtering)
        continue;
      }
      
      const name = String(c.courseName || '').toLowerCase();
      const dept = String(c.department || '').toLowerCase();
      const num = String(c.class_number || '').toLowerCase();
      const base = String(c.baseId || '').toLowerCase();
      const searchFields = [name, dept, num, base];
      
      // Handle colon-separated search (e.g., "640:250")
      let isDirectMatch = false;
      if (term.includes(':')) {
        const parts = term.split(':');
        if (parts.length === 2) {
          const searchDept = parts[0].trim();
          const searchNum = parts[1].trim();
          isDirectMatch = dept.includes(searchDept) && num.includes(searchNum);
        }
      } else {
        // Check for direct substring matches
        isDirectMatch = searchFields.some(field => field.includes(term));
      }
      
      if (isDirectMatch) {
        seen.add(c.baseId);

        const section: ScheduledCourse = {
          id: c.id,
          baseId: c.baseId,
          name: c.courseName,
          campus: c.campus || '',
          day: c.meetingTimes?.[0]?.day || '',
          time: '',
          section: c.section || '',
          instructors: c.instructors || [],
          meetingTimes: c.meetingTimes || [],
          class_number: c.class_number,
          department: c.department,
          location: c.meetingTimes?.[0]?.location,
          credits: c.credits,
          coreRequirements: c.coreRequirements || []
        } as ScheduledCourse;

        directMatches.push({
          id: c.baseId,
          name: c.courseName,
          sections: [section],
          classNumber: c.class_number,
          department: c.department,
          coreRequirements: c.coreRequirements || []
        });
        continue; // Skip further checks for this course
      }
      
      // Try fuzzy matching (only if no direct matches found yet)
      const fuzzyScore = fuzzyMatchScore(term, searchFields, { threshold: 0.6 });
      if (fuzzyScore > 0) {
        seen.add(c.baseId);
        const section: ScheduledCourse = {
          id: c.id,
          baseId: c.baseId,
          name: c.courseName,
          campus: c.campus || '',
          day: c.meetingTimes?.[0]?.day || '',
          time: '',
          section: c.section || '',
          instructors: c.instructors || [],
          meetingTimes: c.meetingTimes || [],
          class_number: c.class_number,
          department: c.department,
          location: c.meetingTimes?.[0]?.location,
          credits: c.credits,
          coreRequirements: c.coreRequirements || []
        } as ScheduledCourse;

        fuzzyMatches.push({
          course: {
            id: c.baseId,
            name: c.courseName,
            sections: [section],
            classNumber: c.class_number,
            department: c.department,
            coreRequirements: c.coreRequirements || []
          },
          score: fuzzyScore
        });
        continue; // Skip partial matching for this course
      }
      
      // Try partial matching (word-by-word)
      const nameMatch = c.courseName && partialMatch(term, c.courseName);
      if (nameMatch) {
        seen.add(c.baseId);
        const section: ScheduledCourse = {
          id: c.id,
          baseId: c.baseId,
          name: c.courseName,
          campus: c.campus || '',
          day: c.meetingTimes?.[0]?.day || '',
          time: '',
          section: c.section || '',
          instructors: c.instructors || [],
          meetingTimes: c.meetingTimes || [],
          class_number: c.class_number,
          department: c.department,
          location: c.meetingTimes?.[0]?.location,
          credits: c.credits,
          coreRequirements: c.coreRequirements || []
        } as ScheduledCourse;

        partialMatches.push({
          id: c.baseId,
          name: c.courseName,
          sections: [section],
          classNumber: c.class_number,
          department: c.department,
          coreRequirements: c.coreRequirements || []
        });
      }
    }

    // Combine results: direct matches first, then fuzzy (sorted by score), then partial
    const out: CourseWithNumber[] = [];
    if (directMatches.length > 0) {
      out.push(...directMatches);
    } else if (fuzzyMatches.length > 0) {
      // Sort fuzzy matches by score (highest first) and add top results
      fuzzyMatches.sort((a, b) => b.score - a.score);
      out.push(...fuzzyMatches.slice(0, 50).map(m => m.course)); // Limit to top 50
    } else if (partialMatches.length > 0) {
      out.push(...partialMatches);
    }

    // Cache the result
    courseSearchCache.set(cacheKey, out);
    return out;
  }, [allCourses, debouncedSearchQuery, selectedCore, completedCourses.length]);

  const getPrerequisiteStatus = (course: CourseWithNumber) => {
    try {
      const first = course.sections?.[0] as any;
      const rc: RutgersCourse = {
        baseId: first.baseId || course.id,
        id: first.id || course.id,
        courseName: course.name,
        fullTitle: course.name,
        department: course.department || first.department,
        class_number: course.classNumber || first.class_number,
        credits: first.credits || '3',
        campus: first.campus || 'NB',
        meetingTimes: first.meetingTimes || [],
        instructors: Array.isArray(first.instructors) ? first.instructors as string[] : [],
        section: first.section || '01',
        subjectNotes: null,
        coreRequirements: course.coreRequirements || [],
        prerequisites: undefined
      };
      const check = checkPrerequisites(rc, completedCourses || []);
      return { isSatisfied: check.isSatisfied, hasPrerequisites: !!rc.prerequisites };
    } catch {
      return { isSatisfied: true, hasPrerequisites: false };
    }
  };

  return {
    searchQuery,
    setSearchQuery,
    selectedCore,
    setSelectedCore,
    searchResults,
    getPrerequisiteStatus,
    openSections,
  };
};

export default function CourseSearch({
  onSelectCourse,
  selectedCourses,
  onRemoveCourse,
  onAddCoreBlock,
  selectedCoreBlocks,
  showSections,
  completedCourses,
  maxResults = 5,
}: CourseSearchProps) {
  const {
    searchQuery,
    setSearchQuery,
    selectedCore,
    setSelectedCore,
    searchResults,
    getPrerequisiteStatus,
    openSections,
  } = useCourseSearch(completedCourses);

  const {
    contextMenu,
    handleContextMenu,
    closeContextMenu,
    handleAddToSniper,
    handleViewInExplore,
    handleCopyCourseInfo,
    handleShareCourse
  } = useCourseContextMenu();

  const handleSelectSearchResult = (course: CourseWithNumber) => {
    onSelectCourse(course);
    setSearchQuery("");
  };

  const convertCourseForContextMenu = (course: CourseWithNumber): ScheduledCourse => {
    // Convert CourseWithNumber to ScheduledCourse for context menu
    const firstSection = course.sections?.[0];
    return {
      id: course.id,
      baseId: course.id,
      name: course.name,
      campus: firstSection?.campus || 'NB',
      day: firstSection?.day || 'TBA',
      time: firstSection?.time || 'TBA',
      section: firstSection?.section || '01',
      instructors: firstSection?.instructors || [],
      meetingTimes: firstSection?.meetingTimes || [],
      class_number: course.classNumber || course.id,
      department: course.id.split(':')[0] || course.id,
      location: firstSection?.location || 'TBA',
      credits: firstSection?.credits || 3,
      coreRequirements: firstSection?.coreRequirements || [],
      baseSectionId: firstSection?.id || course.id,
    };
  };
  
  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col space-y-2">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Search Courses
          </label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, dept:number (e.g. 640:250), or title..."
            className="w-full p-2 border rounded-control dark:bg-slate-700 dark:border-slate-600"
          />
        </div>
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Search Results</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {searchResults.slice(0, maxResults).map((course: CourseWithNumber) => {
              const prereqStatus = getPrerequisiteStatus(course);
              return (
                <button
                  key={course.id}
                  onClick={() => handleSelectSearchResult(course)}
                  onContextMenu={(e) => handleContextMenu(e, convertCourseForContextMenu(course))}
                  className="w-full text-left p-4 border rounded-control hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{course.name}</div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">{course.department}:{course.classNumber}</div>
                    </div>
                  </div>
                </button>
              );
            })}
            {searchResults.length > maxResults && (
              <div className="text-xs text-slate-500 dark:text-slate-400 px-1 py-2">Showing {maxResults} of {searchResults.length} results. Refine your search to narrow down.</div>
            )}
            {searchResults.length === 0 && searchQuery && (
              <p className="text-slate-600 dark:text-slate-400 text-center py-4">
                No courses found matching your search criteria
              </p>
            )}
          </div>
        </div>
      </div>

      <ContextMenu
        isOpen={contextMenu.isOpen}
        x={contextMenu.x}
        y={contextMenu.y}
        course={contextMenu.course}
        onClose={closeContextMenu}
        onAddToSniper={handleAddToSniper}
        onViewInExplore={handleViewInExplore}
        onCopyCourseInfo={handleCopyCourseInfo}
        onShareCourse={handleShareCourse}
      />
    </>
  );
}
